import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { format } from 'date-fns';

import {
  UserRouteV1Model,
  isRouteValidated
} from '@core/models/entities/implementations/user-route-v1';
import {
  CarriedItem,
  JobPlacement,
  PlacementTarget,
  PlanEditGesture
} from '@core/models/plan-edit-gesture.model';
import { ICONS } from '@core/services/icons/icons.service';
import { selectTimelineVisualization } from '@core/store/data-visualization/data-visualization.selectors';
import { PlanEditSessionActions } from '@core/store/plan-edit/plan-edit-session.actions';
import {
  selectPlanWatchForView,
  selectSessionPins
} from '@core/store/plan-edit/plan-edit-session.selectors';
import { moveGesture } from '@core/utils/plan-edit/move-gestures';
import { actionStart } from '@core/utils/plan-edit/route-timing';
import { NEW_ROUTE_ROW, TargetRow, positionsFor } from '@core/utils/plan-edit/targets';

import { SelectItem, SelectValue } from '@shared/components/select/select-item.model';
import { SelectComponent } from '@shared/components/select/select.component';

import { EntityAutoCompleteService } from '@features/components/entities/entity-auto-complete-dialog/entity-auto-complete.service';

import { MoveToRequest, PlanEditFormsService } from '../plan-edit-forms.service';

interface KnownRoute extends TargetRow {
  name: string;
  /** Site names of the route's stops as the timeline shows them, by action uid. */
  siteNames: Record<string, string>;
  startTimes: Record<string, Date>;
}

interface PositionOption {
  target: PlacementTarget;
  site: string | null;
  time: string | null;
}

function samePlacement(a: JobPlacement, b: JobPlacement): boolean {
  return (
    a.kind === b.kind &&
    a.routeUid === b.routeUid &&
    (a.kind === 'EndOf' || a.actionUid === (b as { actionUid: string }).actionUid)
  );
}

/** Moves a stop, a job or a tour to a position picked from a list, on any route of the plan. */
@Component({
  selector: 'app-move-to-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, SelectComponent],
  templateUrl: './move-to-form.component.html',
  styles: ``
})
export class MoveToFormComponent {
  readonly request = input.required<MoveToRequest>();
  readonly done = output<void>();

  private readonly store = inject(Store);
  private readonly picker = inject(EntityAutoCompleteService);
  private readonly injector = inject(Injector);
  private readonly forms = inject(PlanEditFormsService);
  private readonly timeline = this.store.selectSignal(selectTimelineVisualization);
  private readonly watch = this.store.selectSignal(selectPlanWatchForView);
  private readonly pins = this.store.selectSignal(selectSessionPins);

  protected readonly ICONS = ICONS;

  /** Routes the planner reached through the picker, which the timeline may not show. */
  private readonly pickedRouteUids = signal<string[]>([]);

  /** Every route whose content is known: those on screen, then those the live overlay holds. */
  private readonly knownRoutes = computed(() => {
    const known = new Map<string, KnownRoute>();
    for (const row of this.timeline().rows) {
      if (!row.route) continue;
      const siteNames: Record<string, string> = {};
      const startTimes: Record<string, Date> = {};
      for (const task of row.tasks) {
        if (task.kind === 'work' && task.actionUid) {
          siteNames[task.actionUid] = task.name;
          startTimes[task.actionUid] = task.startDate;
        }
      }
      known.set(row.uid, {
        uid: row.uid,
        route: row.route,
        validated: row.validated ?? false,
        name: row.name,
        siteNames,
        startTimes
      });
    }
    const overlay = (this.watch().overlay.upserted['UserRoute'] ?? {}) as unknown as Record<
      string,
      UserRouteV1Model
    >;
    for (const [uid, route] of Object.entries(overlay)) {
      if (known.has(uid) || !route?.actions) continue;
      known.set(uid, {
        uid,
        route,
        validated: isRouteValidated(route),
        name: route.name ?? uid,
        siteNames: {},
        startTimes: {}
      });
    }
    return known;
  });

  protected readonly routeItems = computed<SelectItem[]>(() => {
    const items: SelectItem[] = [...this.knownRoutes().values()].map((known) => ({
      id: known.uid,
      label: known.name,
      icon: ICONS.path,
      disabled: known.validated
    }));
    for (const uid of this.pickedRouteUids()) {
      if (!this.knownRoutes().has(uid))
        items.push({ id: uid, label: uid, icon: ICONS.path });
    }
    return items;
  });

  protected readonly routeUid = signal<string>('');
  protected readonly chosenRouteUid = computed(
    () => this.routeUid() || this.request().carried.routeUid
  );

  protected readonly positions = computed<PositionOption[]>(() => {
    const carried = this.request().carried;
    const uid = this.chosenRouteUid();
    const known = this.knownRoutes().get(uid);
    if (!known) {
      // A route the plan view does not hold has no stops to anchor on, but its end needs none.
      return [
        {
          target: {
            routeUid: uid,
            placement: { kind: 'EndOf', routeUid: uid },
            label: 'end'
          },
          site: null,
          time: null
        }
      ];
    }
    return positionsFor(known, carried).map((target) => {
      const actionUid =
        target.placement.kind === 'EndOf' ? null : target.placement.actionUid;
      const stop = known.route.actions.find((candidate) => candidate.uid === actionUid);
      if (!stop) return { target, site: null, time: null };
      return {
        target,
        site: known.siteNames[stop.uid] ?? stop.site_uid,
        time: format(known.startTimes[stop.uid] ?? actionStart(stop), 'HH:mm')
      };
    });
  });

  /** Live updates rebuild the list, so the chosen placement is found again rather than reset. */
  protected readonly positionIndex = linkedSignal<PositionOption[], number>({
    source: this.positions,
    computation: (positions, previous) => {
      const before = previous?.source[previous.value]?.target.placement;
      if (!before) return 0;
      const index = positions.findIndex(({ target }) =>
        samePlacement(target.placement, before)
      );
      return Math.max(index, 0);
    }
  });
  protected readonly chosen = computed(
    () => this.positions()[this.positionIndex()] ?? null
  );

  protected readonly titleIcon = computed(() => {
    const kind = this.request().carried.kind;
    if (kind === 'job') return ICONS.package;
    return kind === 'tour' ? ICONS.stack : ICONS.arrowsOutCardinal;
  });

  protected onRoute(value: SelectValue): void {
    if (typeof value === 'string') this.routeUid.set(value);
  }

  protected pickOther(): void {
    const picked = signal<string | string[] | null>(null);
    this.picker.show('route', picked, [], false);
    const watcher = effect(
      () => {
        const value = picked();
        if (typeof value === 'string') {
          this.pickedRouteUids.update((uids) => [...uids, value]);
          this.routeUid.set(value);
          watcher.destroy();
        } else if (!this.picker.isVisible()) {
          watcher.destroy();
        }
      },
      { injector: this.injector }
    );
  }

  /** Hands the carried item to the new route form, whose Apply opens the route and moves it in. */
  protected moveToNewRoute(): void {
    const anchorEl = this.forms.state()?.anchorEl;
    const { carried, name } = this.request();
    const then = this.gestureFor(carried, { kind: 'EndOf', routeUid: NEW_ROUTE_ROW });
    if (!anchorEl || !then) return;
    this.forms.open(anchorEl, { kind: 'new-route', then, carriedName: name });
  }

  protected move(): void {
    const chosen = this.chosen();
    if (!chosen) return;
    const carried = this.request().carried;
    const gesture = this.gestureFor(carried, chosen.target.placement);
    if (!gesture) return;
    this.store.dispatch(PlanEditSessionActions.gestureRequested({ gesture }));
    this.done.emit();
  }

  private gestureFor(
    carried: CarriedItem,
    placement: JobPlacement
  ): PlanEditGesture | null {
    const from =
      carried.kind === 'job'
        ? this.knownRoutes()
            .get(carried.routeUid)
            ?.route.actions.find((candidate) => candidate.uid === carried.actionUid)
        : undefined;
    return moveGesture(carried, placement, this.pins(), from);
  }
}
